import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";

import { cn } from "./lib/cn";
import { Spinner } from "./Spinner";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
  /** true iken buton disabled olur ve başında spinner görünür. */
  loading?: boolean;
  /** Metnin SOLUNDAKİ ikon (18px). Varlığı sol boşluğu 16px'ten 14px'e daraltır (§5). */
  icon?: ReactNode;
  /** Metnin SAĞINDAKİ ikon (18px). */
  iconRight?: ReactNode;
}

/**
 * UI Tur 2 §5: `primary` marka dolgusu/beyaz; `secondary` surface+border-default+fg-strong
 * (mockup "Belgeyi Aç", "Filtrele"); `ghost` zeminsiz VE METNİ fg-muted — `fg-strong` tam
 * dolu bir düğmeyle karışırdı, mockup'ta ghost hep ikincil ağırlıkta.
 */
const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-primary text-on-inverse shadow-sm hover:bg-primary-hover focus-visible:outline-primary",
  secondary:
    "border border-border-default bg-surface text-fg-strong shadow-sm hover:border-border-strong hover:bg-canvas focus-visible:outline-primary",
  danger: "bg-danger text-on-inverse shadow-sm hover:bg-danger-hover focus-visible:outline-danger",
  ghost: "bg-transparent text-fg-muted hover:bg-muted focus-visible:outline-primary",
};

/** Yükseklikler §3: sm 36 / md 44 / lg 48. Yatay dolgu tek tip 16px (§5), ikon varsa sol 14px. */
const sizeClasses: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "h-9 text-sm",
  md: "h-11 text-sm",
  lg: "h-12 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled,
    className,
    children,
    type = "button",
    icon,
    iconRight,
    ...rest
  },
  ref,
) {
  const filled = variant === "primary" || variant === "danger";
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors",
        icon || loading ? "pr-4 pl-3.5" : "px-4",
        "focus-visible:outline-2 focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" className={filled ? "border-on-inverse/40 border-t-white" : undefined} />
      ) : icon ? (
        <span aria-hidden="true" className="[&_svg]:size-[18px]">
          {icon}
        </span>
      ) : null}
      {children}
      {iconRight ? (
        <span aria-hidden="true" className="[&_svg]:size-[18px]">
          {iconRight}
        </span>
      ) : null}
    </button>
  );
});
